import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, Pencil, Trash2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import Card3D from './admindashboard/Card3D.js';
import useTheme from './admindashboard/useTheme.js';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';

function AnnouncementsIntroCurtain({ show, isLight }) {
  const accent = isLight ? '#2563eb' : '#fde047';
  const pageBg = isLight ? '#ffffff' : '#0a1e3c';
  const titleColor = isLight ? '#0f172a' : '#ffffff';
  const subtitleColor = isLight ? '#64748b' : 'rgba(147,197,253,0.90)';
  const statusColor = isLight ? '#94a3b8' : 'rgba(253,224,71,0.70)';
  const trackBg = isLight ? 'rgba(15,23,42,0.12)' : 'rgba(255,255,255,0.10)';

  const tileCls = isLight
    ? 'relative grid size-20 place-items-center rounded-[28px] p-2.5 border bg-white'
    : 'relative grid size-20 place-items-center rounded-[28px] bg-white shadow-lifted p-2.5 border';
  const tileStyle = {
    borderColor: isLight ? 'rgba(15,23,42,0.08)' : undefined,
    boxShadow: isLight
      ? '0 2px 4px rgba(15,23,42,0.08), 0 24px 48px -20px rgba(15,23,42,0.22)'
      : undefined,
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="ann-curtain"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06, filter: 'blur(10px)' }}
          transition={{ duration: 0.75, ease: [0.65, 0, 0.35, 1] }}
          className="fixed inset-0 z-[120] grid place-items-center"
          style={{
            background: pageBg,
            color: titleColor,
          }}
        >
          <div className="flex flex-col items-center text-center px-8">
            <motion.span
              initial={{ opacity: 0, scale: 0.6, rotate: -22 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 150, damping: 13, delay: 0.05 }}
              className={tileCls}
              style={tileStyle}
            >
              <motion.span
                className="absolute inset-0 rounded-[28px] border-2"
                style={{ borderColor: accent }}
                initial={{ opacity: 0.9, scale: 1 }}
                animate={{ opacity: 0, scale: 1.55 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeOut' }}
              />
              <img
                src="/iSource LOGO1.png"
                alt="iSource"
                className="w-full h-full object-contain"
              />
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 text-3xl font-semibold tracking-tight"
              style={{ color: titleColor, fontFamily: 'Georgia, Cambria, "Times New Roman", serif' }}
            >
              iSource Announcements
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="mt-2 text-[10px] font-bold tracking-[0.34em] uppercase"
              style={{ color: subtitleColor }}
            >
              Company Broadcast System
            </motion.p>

            <div
              className="relative mt-8 h-px w-56 overflow-hidden"
              style={{ background: trackBg }}
            >
              <motion.span
                className="absolute inset-y-0 left-0"
                style={{ background: accent }}
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 1.15, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-3 text-xs italic"
              style={{ color: statusColor }}
            >
              Preparing notices…
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SkeletonHeaderRow({ index }) {
  const delay = index * 90;
  return (
    <div
      className="stagger-in flex justify-between items-center"
      style={{
        '--i': index,
        animation: `nad-shimmer 1.5s ease-in-out ${delay}ms infinite`,
      }}
    >
      <div
        className="h-8 w-44 rounded-lg"
        style={{ background: 'var(--nad-s2)' }}
      />
      <div
        className="h-10 w-52 rounded-xl"
        style={{ background: 'var(--nad-s2)' }}
      />
    </div>
  );
}

function SkeletonCard({ index, hasImage = false, long = false }) {
  const delay = index * 120;
  const bodyLines = long ? 5 : 3;
  return (
    <div
      className="stagger-in"
      style={{
        '--i': index,
        animation: `nad-shimmer 1.5s ease-in-out ${delay}ms infinite`,
      }}
    >
      <Card3D className="rounded-xl" intensity={5} lift={12}>
        <div
          className="content-glass rounded-xl p-6 relative"
          style={{
            background: 'var(--nad-content-bg)',
            borderColor: 'var(--nad-content-border)',
            borderLeft: '4px solid var(--nad-accent-txt)',
          }}
        >
          <div className="flex justify-between items-start gap-4 mb-4">
            <div className="flex-1 space-y-3">
              <div
                className="h-6 rounded-md w-4/5"
                style={{ background: 'var(--nad-s2)' }}
              />
              <div
                className="h-3 rounded-md w-28"
                style={{ background: 'var(--nad-s2)' }}
              />
            </div>
            <div className="flex-shrink-0 flex flex-col items-end gap-2">
              <div
                className="h-5 w-20 rounded-full"
                style={{ background: 'var(--nad-s2)' }}
              />
              <div
                className="h-5 w-16 rounded-full"
                style={{ background: 'var(--nad-s2)' }}
              />
            </div>
          </div>

          <div
            className="h-3 w-32 rounded-md mb-4"
            style={{ background: 'var(--nad-s2)' }}
          />

          {hasImage && (
            <div
              className="mb-4 rounded-lg overflow-hidden w-full h-40"
              style={{
                border: '1px solid var(--nad-b1)',
                background: `linear-gradient(135deg, var(--nad-s1) 0%, var(--nad-s2) 50%, var(--nad-s1) 100%)`,
              }}
            >
              <div
                className="w-full h-full"
                style={{
                  background:
                    'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.04) 10px, rgba(255,255,255,0.04) 20px)',
                }}
              />
            </div>
          )}

          <div className="space-y-2 mb-4">
            {Array.from({ length: bodyLines }).map((_, i) => {
              const widths = ['w-full', 'w-11/12', 'w-10/12', 'w-4/5', 'w-2/3'];
              return (
                <div
                  key={i}
                  className={`h-3 rounded-md ${widths[i % widths.length]}`}
                  style={{ background: 'var(--nad-s2)' }}
                />
              );
            })}
          </div>

          <div className="flex justify-end gap-2">
            <div
              className="h-8 w-8 rounded-lg"
              style={{ background: 'var(--nad-s2)' }}
            />
            <div
              className="h-8 w-8 rounded-lg"
              style={{ background: 'var(--nad-s2)' }}
            />
          </div>
        </div>
      </Card3D>
    </div>
  );
}

const MarketingAnnouncements = ({ axiosInstance }) => {
  const { isLight } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minElapsed, setMinElapsed] = useState(false);
  const [hasBooted, setHasBooted] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    duration: '',
    image: null,
  });

  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 1250);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (axiosInstance) {
      fetchAnnouncements();
    }
  }, [axiosInstance]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/marketing/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  // Full-screen splash curtain — only visible during first boot.
  // After first dismiss (hasBooted), subsequent refetches fall back to in-place skeletons.
  const showCurtain = !hasBooted && (loading || !minElapsed);

  useEffect(() => {
    if (!loading && minElapsed && !hasBooted) {
      // Small extra window so the curtain's progress bar reaches 100% before exiting
      const t = setTimeout(() => setHasBooted(true), 120);
      return () => clearTimeout(t);
    }
  }, [loading, minElapsed, hasBooted]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({ ...prev, image: e.target.files[0] }));
    }
  };

  const handleCreateClick = () => {
    setEditingAnnouncement(null);
    setFormData({ title: '', body: '', duration: '', image: null });
    setIsModalOpen(true);
  };

  const handleEditClick = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      body: announcement.body,
      duration: announcement.duration,
      image: null,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/marketing/announcements/${id}`);
      setAnnouncements(announcements.filter((a) => a.id !== id));
      toast.success('Announcement deleted successfully');
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAnnouncement) {
        const response = await axiosInstance.put(
          `/marketing/announcements/${editingAnnouncement.id}`,
          {
            title: formData.title,
            body: formData.body,
            duration: formData.duration,
          }
        );
        setAnnouncements(
          announcements.map((a) => (a.id === editingAnnouncement.id ? response.data : a))
        );
        toast.success('Announcement updated successfully');
      } else {
        const data = new FormData();
        data.append('title', formData.title);
        data.append('body', formData.body);
        data.append('duration', formData.duration);
        if (formData.image) {
          data.append('image', formData.image);
        }

        const response = await axiosInstance.post('/marketing/announcements', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setAnnouncements([response.data, ...announcements]);
        toast.success('Announcement created successfully');
      }
      setIsModalOpen(false);
      setEditingAnnouncement(null);
      setFormData({ title: '', body: '', duration: '', image: null });
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error(
        editingAnnouncement ? 'Failed to update announcement' : 'Failed to create announcement'
      );
    }
  };

  const isExpired = (durationDate) => {
    if (!durationDate) return false;
    const endOfDuration = new Date(durationDate);
    endOfDuration.setHours(23, 59, 59, 999);
    return new Date() > endOfDuration;
  };

  return (
    <div className="space-y-6 announcements-nad-scope">
      <AnnouncementsIntroCurtain show={showCurtain} isLight={isLight} />
      {loading && !showCurtain ? (
        <SkeletonHeaderRow index={0} />
      ) : showCurtain ? null : (
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold" style={{ color: 'var(--nad-t1)' }}>
            Announcements
          </h2>
          <Button
            onClick={handleCreateClick}
            className="btn-3d"
            style={{
              background: isLight
                ? 'linear-gradient(135deg, #facc15 0%, #eab308 100%)'
                : 'linear-gradient(135deg, #facc15 0%, #d97706 100%)',
              color: isLight ? '#0f172a' : '#0a1628',
              border: '1px solid',
              borderColor: isLight ? 'rgba(180,83,9,0.30)' : 'rgba(250,204,21,0.25)',
              boxShadow: isLight
                ? '0 4px 16px -4px rgba(180,83,9,0.35), inset 0 1px 0 rgba(255,255,255,0.6)'
                : '0 4px 20px -4px rgba(250,204,21,0.25), inset 0 1px 0 rgba(255,255,255,0.12)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform =
                'perspective(600px) translateZ(10px) scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = '';
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> Create Announcement
          </Button>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent
          className="sm:max-w-[525px]"
          style={{
            background: isLight
              ? '#ffffff'
              : 'linear-gradient(145deg, rgba(58,89,146,0.82) 0%, rgba(38,72,130,0.78) 45%, rgba(30,58,105,0.82) 100%)',
            borderColor: isLight
              ? 'rgba(37,99,235,0.18)'
              : 'rgba(250,204,21,0.32)',
            backdropFilter: 'blur(18px)',
            boxShadow: isLight
              ? '0 30px 80px -10px rgba(37,99,235,0.25), 0 10px 30px -8px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.9)'
              : '0 30px 80px -10px rgba(0,0,0,0.55), 0 0 0 1px rgba(59,130,246,0.12), 0 0 40px rgba(59,130,246,0.18), inset 0 1px 0 rgba(250,204,21,0.20), inset 0 0 60px rgba(59,130,246,0.10)',
          }}
        >
          <DialogHeader>
            <DialogTitle
              style={{
                color: 'var(--nad-t1)',
                fontSize: '1.4rem',
                fontWeight: 700,
                letterSpacing: '0.01em',
              }}
            >
              {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
            </DialogTitle>
            <DialogDescription className="sr-only">Marketing announcement form</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="title"
                style={{
                  color: isLight ? '#2563eb' : 'var(--nad-accent-txt)',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}
              >
                Announcement Title
              </Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                maxLength={100}
                placeholder="Enter title (max 100 characters)"
                required
                className="whitespace-normal"
                style={{
                  background: isLight
                    ? 'rgba(255,255,255,0.85)'
                    : 'rgba(255,255,255,0.10)',
                  borderColor: isLight
                    ? 'rgba(37,99,235,0.22)'
                    : 'rgba(250,204,21,0.25)',
                  color: 'var(--nad-t1)',
                  boxShadow: isLight
                    ? 'inset 0 1px 2px rgba(15,23,42,0.04)'
                    : 'inset 0 1px 0 rgba(255,255,255,0.08)',
                  minHeight: '42px',
                }}
              />
              <p
                className="text-xs text-right"
                style={{
                  color: isLight ? 'rgba(15,23,42,0.45)' : 'rgba(255,255,255,0.40)',
                }}
              >
                {formData.title.length}/100
              </p>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="body"
                style={{
                  color: isLight ? '#2563eb' : 'var(--nad-accent-txt)',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}
              >
                Announcement Body
              </Label>
              <Textarea
                id="body"
                name="body"
                value={formData.body}
                onChange={handleInputChange}
                maxLength={2000}
                placeholder="Enter announcement details..."
                required
                className="min-h-[100px] resize-y whitespace-pre-wrap"
                style={{
                  background: isLight
                    ? 'rgba(255,255,255,0.85)'
                    : 'rgba(255,255,255,0.10)',
                  borderColor: isLight
                    ? 'rgba(37,99,235,0.22)'
                    : 'rgba(250,204,21,0.25)',
                  color: 'var(--nad-t1)',
                  boxShadow: isLight
                    ? 'inset 0 1px 2px rgba(15,23,42,0.04)'
                    : 'inset 0 1px 0 rgba(255,255,255,0.08)',
                }}
              />
              <p
                className="text-xs text-right"
                style={{
                  color: isLight ? 'rgba(15,23,42,0.45)' : 'rgba(255,255,255,0.40)',
                }}
              >
                {formData.body.length}/2000
              </p>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="duration"
                style={{
                  color: isLight ? '#2563eb' : 'var(--nad-accent-txt)',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}
              >
                Duration (Valid Until)
              </Label>
              <Input
                id="duration"
                name="duration"
                type="date"
                value={formData.duration}
                onChange={handleInputChange}
                required
                style={{
                  background: isLight
                    ? 'rgba(255,255,255,0.85)'
                    : 'rgba(255,255,255,0.10)',
                  borderColor: isLight
                    ? 'rgba(37,99,235,0.22)'
                    : 'rgba(250,204,21,0.25)',
                  color: 'var(--nad-t1)',
                  boxShadow: isLight
                    ? 'inset 0 1px 2px rgba(15,23,42,0.04)'
                    : 'inset 0 1px 0 rgba(255,255,255,0.08)',
                  minHeight: '42px',
                }}
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="image"
                style={{
                  color: isLight ? '#2563eb' : 'var(--nad-accent-txt)',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}
              >
                Announcement Image (Optional)
              </Label>
              <Input
                id="image"
                name="image"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{
                  background: isLight
                    ? 'rgba(255,255,255,0.85)'
                    : 'rgba(255,255,255,0.10)',
                  borderColor: isLight
                    ? 'rgba(37,99,235,0.22)'
                    : 'rgba(250,204,21,0.25)',
                  color: 'var(--nad-t1)',
                  boxShadow: isLight
                    ? 'inset 0 1px 2px rgba(15,23,42,0.04)'
                    : 'inset 0 1px 0 rgba(255,255,255,0.08)',
                  minHeight: '42px',
                }}
              />
            </div>

            <DialogFooter>
              <Button
                type="submit"
                className="btn-3d"
                style={{
                  background: isLight
                    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)'
                    : 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #1e40af 100%)',
                  color: '#ffffff',
                  border: '1px solid',
                  borderColor: isLight ? 'rgba(59,130,246,0.45)' : 'rgba(250,204,21,0.35)',
                  boxShadow: isLight
                    ? '0 6px 20px -6px rgba(37,99,235,0.55), inset 0 1px 0 rgba(255,255,255,0.45)'
                    : '0 6px 24px -6px rgba(59,130,246,0.45), 0 0 20px rgba(59,130,246,0.18), inset 0 1px 0 rgba(255,255,255,0.18)',
                  minHeight: '40px',
                  paddingLeft: '1.25rem',
                  paddingRight: '1.25rem',
                  fontWeight: 600,
                }}
              >
                {editingAnnouncement ? 'Save Changes' : 'Post Announcement'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4 max-w-2xl mx-auto">
        {loading ? (
          <>
            <SkeletonCard index={1} hasImage long />
            <SkeletonCard index={2} />
            <SkeletonCard index={3} long />
          </>
        ) : announcements.length === 0 ? (
          <div className="stagger-in" style={{ '--i': 0 }}>
            <Card3D className="rounded-xl p-6 h-full" intensity={4} lift={8}>
              <div
                className="content-glass rounded-xl flex flex-col items-center justify-center py-10 px-6 text-center"
                style={{
                  background: 'var(--nad-content-bg)',
                  borderColor: 'var(--nad-content-border)',
                }}
              >
                <div
                  className="w-16 h-16 rounded-2xl mb-4 flex items-center justify-center"
                  style={{
                    background: isLight
                      ? 'linear-gradient(135deg, rgba(250,204,21,0.20), rgba(37,99,235,0.12))'
                      : 'linear-gradient(135deg, rgba(250,204,21,0.15), rgba(59,130,246,0.08))',
                  }}
                >
                  <Plus size={28} style={{ color: 'var(--nad-accent-txt)' }} />
                </div>
                <p className="text-base font-semibold mb-1" style={{ color: 'var(--nad-t1)' }}>
                  No announcements yet
                </p>
                <p className="text-sm" style={{ color: 'var(--nad-t3)' }}>
                  Create your first company-wide announcement to get started.
                </p>
              </div>
            </Card3D>
          </div>
        ) : (
          announcements.map((announcement, idx) => {
            const expired = isExpired(announcement.duration);
            return (
              <div
                key={announcement.id}
                className="stagger-in"
                style={{ '--i': idx }}
              >
                <Card3D className="rounded-xl" intensity={5} lift={12}>
                  <div
                    className={`content-glass rounded-xl p-6 relative ${
                      expired
                        ? ''
                        : ''
                    }`}
                    style={{
                      background: 'var(--nad-content-bg)',
                      borderColor: expired ? 'var(--nad-b1)' : 'var(--nad-content-border)',
                      borderLeft: `4px solid ${
                        expired ? 'var(--nad-t4)' : 'var(--nad-accent-txt)'
                      }`,
                      opacity: expired ? 0.72 : 1,
                    }}
                  >
                    <div className="flex justify-between items-start gap-4 mb-4">
                      <h3
                        className="text-lg font-semibold break-words w-full"
                        style={{ color: 'var(--nad-t1)' }}
                      >
                        {announcement.title}
                      </h3>
                      <div className="flex-shrink-0 flex flex-col items-end gap-2">
                        {announcement.created_by && (
                          <span
                            className="text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{
                              background: 'var(--nad-s1)',
                              color: 'var(--nad-t3)',
                              border: '1px solid',
                              borderColor: 'var(--nad-b1)',
                            }}
                          >
                            {announcement.created_by}
                          </span>
                        )}
                        {expired ? (
                          <span
                            className="text-xs font-medium px-2.5 py-0.5 rounded border"
                            style={{
                              background: isLight
                                ? 'rgba(225,29,72,0.10)'
                                : 'rgba(251,113,133,0.10)',
                              color: 'var(--nad-neg)',
                              borderColor: isLight
                                ? 'rgba(225,29,72,0.30)'
                                : 'rgba(251,113,133,0.25)',
                            }}
                          >
                            Inactive
                          </span>
                        ) : (
                          <span
                            className="text-xs font-medium px-2.5 py-0.5 rounded border"
                            style={{
                              background: isLight
                                ? 'rgba(5,150,105,0.10)'
                                : 'rgba(52,211,153,0.10)',
                              color: 'var(--nad-pos)',
                              borderColor: isLight
                                ? 'rgba(5,150,105,0.30)'
                                : 'rgba(52,211,153,0.25)',
                            }}
                          >
                            Active
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs mb-4" style={{ color: 'var(--nad-t4)' }}>
                      Valid until: {new Date(announcement.duration).toLocaleDateString()}
                    </p>
                    {announcement.image_url && (
                      <div className="mb-4 rounded-lg overflow-hidden" style={{ border: '1px solid var(--nad-b1)' }}>
                        <img
                          src={announcement.image_url}
                          alt={announcement.title}
                          className="w-full h-auto"
                          style={{ display: 'block' }}
                        />
                      </div>
                    )}
                    <p
                      className="whitespace-pre-wrap break-words text-sm mb-4"
                      style={{ color: 'var(--nad-t2)' }}
                    >
                      {announcement.body}
                    </p>
                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        onClick={() => handleEditClick(announcement)}
                        className="btn-3d h-8 w-8 rounded-lg flex items-center justify-center"
                        style={{
                          color: isLight ? '#2563eb' : '#60a5fa',
                          background: 'var(--nad-s1)',
                          border: '1px solid var(--nad-b1)',
                        }}
                        title="Edit announcement"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            className="btn-3d h-8 w-8 rounded-lg flex items-center justify-center"
                            style={{
                              color: 'var(--nad-neg)',
                              background: 'var(--nad-s1)',
                              border: '1px solid var(--nad-b1)',
                            }}
                            title="Delete announcement"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent
                          style={{
                            background: isLight
                              ? '#ffffff'
                              : 'linear-gradient(145deg, rgba(58,89,146,0.82) 0%, rgba(38,72,130,0.78) 45%, rgba(30,58,105,0.82) 100%)',
                            borderColor: isLight
                              ? 'rgba(225,29,72,0.18)'
                              : 'rgba(251,113,133,0.35)',
                            backdropFilter: 'blur(18px)',
                            boxShadow: isLight
                              ? '0 30px 80px -10px rgba(225,29,72,0.22), 0 10px 30px -8px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.9)'
                              : '0 30px 80px -10px rgba(0,0,0,0.55), 0 0 0 1px rgba(239,68,68,0.10), 0 0 40px rgba(239,68,68,0.15), inset 0 1px 0 rgba(251,113,133,0.18), inset 0 0 60px rgba(59,130,246,0.08)',
                          }}
                        >
                          <AlertDialogHeader>
                            <AlertDialogTitle
                              style={{
                                color: 'var(--nad-t1)',
                                fontSize: '1.25rem',
                                fontWeight: 700,
                              }}
                            >
                              Delete Announcement?
                            </AlertDialogTitle>
                            <AlertDialogDescription
                              style={{
                                color: isLight
                                  ? 'rgba(15,23,42,0.70)'
                                  : 'rgba(255,255,255,0.78)',
                                lineHeight: 1.6,
                              }}
                            >
                              Are you sure you want to delete "{announcement.title}"? This action
                              cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel
                              style={{
                                background: isLight
                                  ? 'rgba(255,255,255,0.90)'
                                  : 'rgba(255,255,255,0.10)',
                                borderColor: isLight
                                  ? 'rgba(15,23,42,0.16)'
                                  : 'rgba(255,255,255,0.18)',
                                color: 'var(--nad-t2)',
                                minHeight: '38px',
                                paddingLeft: '1rem',
                                paddingRight: '1rem',
                                boxShadow: isLight
                                  ? 'inset 0 1px 0 rgba(255,255,255,0.95)'
                                  : 'inset 0 1px 0 rgba(255,255,255,0.10)',
                              }}
                            >
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(announcement.id)}
                              style={{
                                background: isLight
                                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)'
                                  : 'linear-gradient(135deg, #f87171 0%, #ef4444 50%, #b91c1c 100%)',
                                color: '#ffffff',
                                border: '1px solid',
                                borderColor: isLight
                                  ? 'rgba(220,38,38,0.45)'
                                  : 'rgba(251,113,133,0.40)',
                                boxShadow: isLight
                                  ? '0 6px 20px -6px rgba(220,38,38,0.55), inset 0 1px 0 rgba(255,255,255,0.30)'
                                  : '0 6px 24px -6px rgba(239,68,68,0.55), 0 0 18px rgba(239,68,68,0.18), inset 0 1px 0 rgba(255,255,255,0.18)',
                                minHeight: '38px',
                                paddingLeft: '1rem',
                                paddingRight: '1rem',
                                fontWeight: 600,
                              }}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </Card3D>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MarketingAnnouncements;
